import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import vaulttablecolumnService from '../services/vaulttablecolumnService';
import vaulttableService from '../services/vaulttableService';
import vaultsourceService from '../services/vaultsourceService';
import './VaultTableColumnForm.css';

function VaultTableColumnForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id, vaultsourceid, vaulttableid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vaultSourceName, setVaultSourceName] = useState('');
  const [vaultTableName, setVaultTableName] = useState('');
  const [vaultSourceId, setVaultSourceId] = useState(null);
  const [formData, setFormData] = useState({
    vaulttableid: '',
    name: '',
    encrypted: 'Y',
    comments: '',
    datatype: ''
  });

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    // Auto-populate vaulttableid if creating from vault table page
    if (vaulttableid && isCreateMode) {
      console.log('Setting vaulttableid from params:', vaulttableid);
      setFormData(prev => ({ ...prev, vaulttableid: parseInt(vaulttableid) }));
    }
  }, [vaulttableid, isCreateMode]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchVaultTableColumn();
    }
  }, [id, isEditMode, isViewMode]);

  useEffect(() => {
    // Fetch vault table when vaulttableid is set
    if (formData.vaulttableid && (isEditMode || isViewMode)) {
      fetchVaultTable(formData.vaulttableid);
    }
  }, [formData.vaulttableid, isEditMode, isViewMode]);

  const fetchVaultTable = async (vaulttableid) => {
    try {
      const response = await vaulttableService.getVaultTableById(vaulttableid);
      const vaulttableData = response.data || response;
      setVaultTableName(vaulttableData.name || '');

      // Fetch vault source if available
      if (vaulttableData.vaultsourceid) {
        setVaultSourceId(vaulttableData.vaultsourceid);
        fetchVaultSource(vaulttableData.vaultsourceid);
      }
    } catch (err) {
      console.error('Failed to fetch vault table:', err);
    }
  };

  const fetchVaultSource = async (vaultsourceid) => {
    try {
      const response = await vaultsourceService.getVaultSourceById(vaultsourceid);
      const vaultsourceData = response.data || response;
      setVaultSourceName(vaultsourceData.name || '');
    } catch (err) {
      console.error('Failed to fetch vault source:', err);
    }
  };

  const fetchVaultTableColumn = async () => {
    try {
      setLoading(true);
      const response = await vaulttablecolumnService.getVaultTableColumnById(id);
      const vaulttablecolumnData = response.data || response;
      setFormData(vaulttablecolumnData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch vault table column');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.vaulttableid) {
      setError('Vault System Table ID is required');
      setLoading(false);
      return;
    }

    if (!formData.name) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting vault table column data:', formData);

      if (isCreateMode) {
        const response = await vaulttablecolumnService.createVaultTableColumn(formData);
        console.log('Vault table column created:', response);
      } else if (isEditMode) {
        const response = await vaulttablecolumnService.updateVaultTableColumn(id, formData);
        console.log('Vault table column updated:', response);
      }

      // Navigate back to vault table view
      const targetVaultTableId = vaulttableid || formData.vaulttableid;
      const targetVaultSourceId = vaultsourceid || vaultSourceId;
      if (targetVaultTableId && targetVaultSourceId) {
        navigate(`/admin/vaults/${targetVaultSourceId}/vaulttable/${targetVaultTableId}`);
      } else if (targetVaultTableId) {
        navigate(`/admin/vaulttable/${targetVaultTableId}`);
      } else {
        navigate('/admin/vaults');
      }
    } catch (err) {
      console.error('Error saving vault table column:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save vault table column');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const targetVaultTableId = vaulttableid || formData.vaulttableid;
    const targetVaultSourceId = vaultsourceid || vaultSourceId;
    if (targetVaultTableId && targetVaultSourceId) {
      navigate(`/admin/vaults/${targetVaultSourceId}/vaulttable/${targetVaultTableId}`);
    } else if (targetVaultTableId) {
      navigate(`/admin/vaulttable/${targetVaultTableId}`);
    } else {
      navigate('/admin/vaults');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this vault table column?')) {
      return;
    }

    try {
      setLoading(true);
      await vaulttablecolumnService.deleteVaultTableColumn(id);
      const targetVaultTableId = vaulttableid || formData.vaulttableid;
      const targetVaultSourceId = vaultsourceid || vaultSourceId;
      if (targetVaultTableId && targetVaultSourceId) {
        navigate(`/admin/vaults/${targetVaultSourceId}/vaulttable/${targetVaultTableId}`);
      } else if (targetVaultTableId) {
        navigate(`/admin/vaulttable/${targetVaultTableId}`);
      } else {
        navigate('/admin/vaults');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault table column');
      setLoading(false);
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="vaulttablecolumn-form-container">Loading vault table column...</div>;
  }

  return (
    <div className="vaulttablecolumn-form-container">
      {/* Breadcrumb */}
      {(isViewMode || isEditMode) && (vaultSourceName || vaultTableName || formData.name) && (
        <div className="breadcrumb">
          <Link to="/admin/vaults" className="breadcrumb-link">
            Vault Systems
          </Link>
          <span className="breadcrumb-separator">/</span>
          {vaultSourceName && (vaultsourceid || vaultSourceId) && (
            <>
              <Link to={`/admin/vaults/${vaultsourceid || vaultSourceId}`} className="breadcrumb-link">
                {vaultSourceName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          {vaultTableName && vaulttableid && (
            <>
              <Link to={`/admin/vaults/${vaultsourceid || vaultSourceId}/vaulttable/${vaulttableid}`} className="breadcrumb-link">
                {vaultTableName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">{formData.name}</span>
        </div>
      )}

      <h2>
        {isCreateMode && 'Create New Vault System Table Column'}
        {isEditMode && 'Edit Vault System Table Column'}
        {isViewMode && 'View Vault System Table Column'}
      </h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="datatype">Data Type</label>
            <input
              type="text"
              id="datatype"
              name="datatype"
              value={formData.datatype || ''}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="e.g., VARCHAR(255), INT, TEXT"
            />
          </div>

          <div className="form-group">
            <label htmlFor="encrypted">Encrypted</label>
            <select
              id="encrypted"
              name="encrypted"
              value={formData.encrypted || 'N'}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="N">No (N)</option>
              <option value="Y">Yes (Y)</option>
            </select>
          </div>

          <div className="form-group full-width">
            <label htmlFor="comments">Comments</label>
            <textarea
              id="comments"
              name="comments"
              value={formData.comments || ''}
              onChange={handleChange}
              disabled={isViewMode}
              rows="4"
              placeholder="Additional comments or notes about this column"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {isViewMode ? (
            <>
              <button type="button" onClick={() => navigate(`/admin/vaults/${vaultsourceid || vaultSourceId}/vaulttable/${vaulttableid || formData.vaulttableid}/vaulttablecolumn/${id}/edit`)} title="Edit Vault System Table Column">
                ✏️ Edit
              </button>
              <button type="button" onClick={handleDelete} className="btn-danger" title="Delete Vault System Table Column">
                🗑️ Delete
              </button>
              <button type="button" onClick={handleCancel} title="Back to Vault System Table">
                ← Back to Vault System Table
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Vault System Table Column' : 'Update Vault System Table Column'}
              </button>
              <button type="button" onClick={handleCancel} disabled={loading} title="Cancel">
                ← Cancel
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default VaultTableColumnForm;
